import { mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import {
  atan,
  attribute,
  cameraViewMatrix,
  clamp,
  cos,
  cross,
  dFdx,
  dFdy,
  dot,
  Fn,
  float,
  floor,
  fract,
  If,
  instancedArray,
  instanceIndex,
  ivec2,
  length,
  max,
  mix,
  modelWorldMatrix,
  mx_noise_float,
  normalize,
  positionWorld,
  sign,
  sin,
  smoothstep,
  step,
  textureLoad,
  uint,
  uniform,
  varyingProperty,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import { hashSeed } from '../math/rng';
import { registerVolumeMaterial, sceneSurfacePosition } from '../render/lightVolumes';
import { mouthClearance, type TerrainMouth } from '../terrain/TerrainMouth';
import { paletteHeight, type TerrainProfile } from '../terrain/TerrainProfile';
import { nextStamp, type TerrainQuadtree, type TNode } from '../terrain/TerrainQuadtree';
import { surfacePoint } from '../terrain/TerrainShape';
import { noise3When, noiseWhen, type TerrainStyle } from '../terrain/TerrainStyle';
import { TILE_RES, type TileGenerator } from '../terrain/TileGenerator';
import type { CanopyShadow } from './CanopyShadow';
import { cellRandoms } from './cellRandom';
import { NearestNodes } from './NearestNodes';
import { generateTree, type TreeTemplate } from './treeGen';
import type { SpeciesStyle, TreeShape, TreeStyle } from './VegetationStyle';

type N = any;
const GRID_T = 12; // tree cells per node edge
const CPN = GRID_T * GRID_T;
const MAX_NODES = 160;
const RANGE = 520;
/** vec4s the placement pass writes per cell (see buildPlacement). */
const CELL_VEC4 = 5;
/**
 * How much each tree's own build may differ from its species' (see treeGen): height and width
 * up to this share either way, and a lean of up to LEAN (radians) off its up.
 */
const VARY_TALL = 0.14;
const VARY_WIDE = 0.12;
const LEAN = 0.07;

const _rel = new THREE.Vector3();
const _cam = new THREE.Vector3();

/**
 * Forests near the camera, placed on the GPU per terrain node (one level above the
 * finest) with the same forest mask the terrain shader uses, so 3D trees appear
 * exactly where forests read as dark canopy from across the hollow.
 */
export class TreeRenderer {
  readonly group = new THREE.Group();
  /** Light volumes of glowing species, drawn in the deferred light pass (see lightVolumes). */
  readonly lightGroup = new THREE.Group();
  /** The crowns' discs for the canopy shade map (see CanopyShadow), when it's given one. */
  readonly canopyGroup = new THREE.Group();
  private readonly nodes: N;
  /** Each cell's tree, placed by the compute pass (CELL_VEC4 vec4s a cell; see buildPlacement). */
  private readonly cells: N;
  private readonly cellCount = uniform(0, 'uint');
  private readonly place: N;
  private compiled: Promise<void> | null = null;
  private placeReady = false;
  private readonly meshes: THREE.Mesh[] = [];
  private readonly time = uniform(0);
  private readonly cameraLocal = uniform(new THREE.Vector3());
  count = 0;
  readonly level: number;
  private readonly range: number;
  private readonly pick = new NearestNodes(MAX_NODES);
  /** Trees fade out by this distance: the range, or nearer when the nearest nodes fill the buffer. */
  private readonly fadeEnd: N;
  /**
   * Nodes (at the tree level) this layer may have trees on, if not all: a biome's layer skips
   * ground its biome can't reach, since every node drawn costs its whole grid of trees.
   */
  keep: ((node: TNode) => boolean) | null = null;

  constructor(
    private readonly gen: TileGenerator,
    private readonly tree: TerrainQuadtree,
    private readonly hollowCenter: THREE.Vector3,
    private readonly anchor: THREE.Vector3,
    profile: TerrainProfile,
    terrainStyle: TerrainStyle,
    style: TreeStyle,
    /** Cave mouths to keep clear of. */
    private readonly mouths: TerrainMouth[] = [],
    /** This layer's own random stream (see cellRandoms): layers on one grid must differ. */
    private readonly salt = 0,
    /** The map of the crowns' shade to draw this layer's crowns into (none: they cast none). */
    private readonly canopy: CanopyShadow | null = null,
    /**
     * The world's seed. Its species are generated from it (treeGen), and where trees stand is
     * mixed with it: the same seed gives the same trees in the same places, for everyone.
     */
    seed = 0,
  ) {
    this.world = worldSalt(seed);
    this.level = tree.maxLevel - 1;
    this.range = style.range ?? RANGE;
    this.fadeEnd = uniform(this.range);
    this.nodes = instancedArray(MAX_NODES * 3, 'vec4');
    this.cells = instancedArray(MAX_NODES * CPN * CELL_VEC4, 'vec4');
    this.place = this.buildPlacement(profile, terrainStyle, style).compute(MAX_NODES * CPN);
    for (const [i, species] of [style.a, style.b].entries()) {
      const template = generateTree(species.shape, hashSeed(seed >>> 0, salt, i));
      const geo = templateGeometry(template);
      const mesh = new THREE.Mesh(geo, this.buildMaterial(species, i === 0, 'tree', template.crown));
      mesh.frustumCulled = false;
      mesh.name = `trees-${species.shape}`;
      this.meshes.push(mesh);
      this.group.add(mesh);
      if (species.light && species.glow) {
        const light = new THREE.Mesh(
          buildLightGeometry(species.light.radius, species.light.height),
          this.buildMaterial(species, i === 0, 'light', template.crown),
        );
        light.frustumCulled = false;
        light.name = `lights-`;
        this.meshes.push(light);
        this.lightGroup.add(light);
      }
      const shade = template.crown;
      if (shade && canopy) {
        const disc = new THREE.Mesh(
          buildCrownDisc(shade),
          this.buildMaterial(species, i === 0, 'canopy', shade),
        );
        disc.frustumCulled = false;
        disc.name = `canopy-${species.shape}`;
        this.meshes.push(disc);
        this.canopyGroup.add(disc);
      }
    }
  }

  /** The world seed's share of the placement hash (see cellRandoms). */
  private readonly world: number;

  /** Draw this layer (a biome's layer is switched off while its biome is nowhere near). */
  set active(on: boolean) {
    this.group.visible = on;
    this.lightGroup.visible = on;
    this.canopyGroup.visible = on;
  }

  get active(): boolean {
    return this.group.visible;
  }

  /**
   * Compile the placement pass in the background (once). Until it's ready no trees are drawn:
   * a first dispatch would compile it synchronously and stall a frame.
   */
  precompile(): Promise<void> {
    this.compiled ??= this.gen.renderer.compileComputeAsync(this.place).then(() => {
      this.placeReady = true;
    });
    return this.compiled;
  }

  update(cameraUniverse: THREE.Vector3, dt: number): void {
    this.time.value += dt;
    this.cameraLocal.value.subVectors(cameraUniverse, this.anchor);
    _rel.subVectors(this.hollowCenter, this.anchor);
    // Horizontal distance: the camera projected onto the reference surface of the node
    // centres (the ground can be far above it).
    surfacePoint(this.tree.shape, _cam.subVectors(cameraUniverse, this.hollowCenter), _cam);
    // Unique ancestors at the tree level of every drawn node in range, the nearest first.
    // Up each node's parent links (the walk that drew it set them), taking each ancestor
    // once. By key, through a Set and the tree's map, every key was a boxed number (they
    // don't fit a small integer), as was every read through Vector3's shared methods.
    const pick = this.pick;
    pick.reset();
    const stamp = nextStamp();
    const cx = _cam.x;
    const cy = _cam.y;
    const cz = _cam.z;
    for (const d of this.tree.drawList) {
      let anc = d.node;
      while (anc.level > this.level && anc.parent) anc = anc.parent;
      if (anc.level !== this.level || anc.mark === stamp) continue;
      anc.mark = stamp;
      if (anc.state !== 'ready' || anc.slot < 0) continue;
      const p = anc.point;
      const dx = p.x - cx;
      const dy = p.y - cy;
      const dz = p.z - cz;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) - anc.halfDiag;
      if (dist <= this.range && (this.keep === null || this.keep(anc))) pick.add(anc, dist);
    }
    const count = pick.finish();
    // Fade out before the nodes left out, so turning (which changes what is in view, and so
    // where the cut falls) doesn't pop trees in and out.
    this.fadeEnd.value = Math.min(this.range, pick.cut);
    const arr = this.nodes.value.array as Float32Array;
    let i = 0;
    for (let k = 0; k < count; k++) {
      const node = pick.node(k);
      const size = 2 / 2 ** node.level;
      const sx = this.gen.slotX(node.slot);
      const sy = this.gen.slotY(node.slot);
      const o = i * 12;
      arr[o] = node.point.x + _rel.x;
      arr[o + 1] = node.point.y + _rel.y;
      arr[o + 2] = node.point.z + _rel.z;
      arr[o + 3] = 0;
      arr[o + 4] = -1 + (node.x + 0.5) * size;
      arr[o + 5] = -1 + (node.y + 0.5) * size;
      arr[o + 6] = size;
      arr[o + 7] = node.face;
      arr[o + 8] = sx;
      arr[o + 9] = sy;
      arr[o + 10] = 0;
      arr[o + 11] = 0;
      i++;
    }
    if (!this.placeReady) {
      void this.precompile();
      i = 0;
    }
    this.count = i;
    this.cellCount.value = i * CPN;
    if (i > 0) {
      this.nodes.value.needsUpdate = true;
      this.gen.renderer.compute(this.place);
    }
    for (const m of this.meshes) (m.geometry as THREE.InstancedBufferGeometry).instanceCount = i * CPN;
  }

  /**
   * The placement pass: one thread a cell, once a frame. It finds the cell's ground (height,
   * slope, biome, landform), decides whether a tree grows there and which species, and writes
   * what the vertex shader needs (CELL_VEC4 vec4s):
   *   0: base (anchor-relative), yaw      1: up, tint
   *   2: tangent t1, sway                 3: ground point (hollow-local), signed scale
   *   4: its own build (three numbers in 0..1: height, width, lean)
   * The scale is positive for species A, negative for B, 0 where nothing grows. Done per vertex,
   * every vertex of every tree repeated all of this.
   */
  private buildPlacement(profile: TerrainProfile, terrainStyle: TerrainStyle, style: TreeStyle): N {
    const atlas = this.gen.atlas;
    const biomeAtlas = this.gen.biomeAtlas;
    const surfaceAtlas = this.gen.surfaceAtlas;
    const shape = this.tree.shape.gpu;
    const nodes = this.nodes;
    const cells = this.cells;
    const time = this.time;
    const cameraLocal = this.cameraLocal;

    const sampleTile = (slot: N, g: N, tex: N = atlas) => {
      const t = g.mul(TILE_RES - 1);
      const t0 = clamp(floor(t), vec2(0), vec2(TILE_RES - 2));
      const f = t.sub(t0);
      const base = slot.add(t0);
      const a = textureLoad(tex, ivec2(base));
      const b = textureLoad(tex, ivec2(base.add(vec2(1, 0))));
      const c = textureLoad(tex, ivec2(base.add(vec2(0, 1))));
      const d = textureLoad(tex, ivec2(base.add(vec2(1, 1))));
      return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
    };

    return Fn(() => {
      const idx = instanceIndex;
      If(idx.lessThan(this.cellCount), () => {
        const node = idx.div(uint(CPN));
        const cell = idx.sub(node.mul(uint(CPN)));
        const d0 = nodes.element(node.mul(uint(3)));
        const d1 = nodes.element(node.mul(uint(3)).add(uint(1)));
        const d2 = nodes.element(node.mul(uint(3)).add(uint(2)));
        const uc = d1.x;
        const vc = d1.y;
        const size = d1.z;
        const rnd = cellRandoms(cell, CPN, d1, this.salt, this.world);
        const jitter = vec2(rnd(0), rnd(1)).sub(0.5).mul(0.9);
        const gi = vec2(float(cell.mod(uint(GRID_T))), float(cell.div(uint(GRID_T))));
        const g = gi.add(0.5).add(jitter).div(GRID_T);
        const surf = shape.at(d1.w, uc, vc, size, g);
        const dir = surf.normal;
        const tile = sampleTile(d2.xy, g);
        const h = tile.x;
        const slope = length(vec2(tile.z, tile.w));
        const base = d0.xyz.add(surf.offset).sub(dir.mul(max(h, 0)));
        const up = dir.negate();
        const t1 = normalize(surf.U.sub(dir.mul(dot(surf.U, dir))));

        // Same forest mask as the terrain shader (hollow-local position noise).
        const p = surf.local.sub(dir.mul(max(h, 0)));
        const minH = style.minHeight ?? 6;
        const hp = paletteHeight(profile, h);
        const above = smoothstep(minH, minH * 2, hp);
        const biome = biomeAtlas ? sampleTile(d2.xy, g, biomeAtlas) : undefined;
        const ridge = surfaceAtlas ? sampleTile(d2.xy, g, surfaceAtlas).xyz.mul(2).sub(1) : undefined;
        const layer = { p, h: hp, slope, profile, biome, ridge };
        const mask = style.mask ? style.mask(layer) : terrainStyle.forestMask(layer);
        const where: N = style.where ? style.where(layer) : float(1);
        const forest = mask.mul(above).mul(where);
        // A few lone trees in open ground too.
        const lone = style.loneTrees;
        const meadowTree = smoothstep(1 - lone * 1.5, 1 - lone * 0.5, rnd(2))
          .mul(above)
          .mul(where)
          .mul(float(1).sub(smoothstep(0.15, 0.3, slope)));
        const present = max(forest.mul(smoothstep(0.15, 0.55, rnd(3).mul(forest))), meadowTree);
        const aShare = mix(float(style.aShare[0]), float(style.aShare[1]), smoothstep(150, 500, hp));
        const pickA = rnd(4).lessThan(aShare);
        const dist = length(base.sub(cameraLocal));
        const fade = float(1).sub(smoothstep(this.fadeEnd.mul(0.75), this.fadeEnd, dist));
        const scale = smoothstep(0.3, 0.6, present)
          .mul(fade)
          .mul(rnd(5).mul(0.6).add(0.75))
          .mul(mouthClearance(p, this.mouths, 3, 8, 0, MOUTH_PATH));
        const yaw = rnd(6).mul(Math.PI * 2);
        // Gentle sway (the vertex shader makes it stronger toward the crown).
        const sway: N = mx_noise_float(base.mul(0.02).add(vec3(time.mul(0.25), 0, 0)));
        const o = idx.mul(uint(CELL_VEC4));
        cells.element(o).assign(vec4(base, yaw));
        cells.element(o.add(uint(1))).assign(vec4(up, rnd(7)));
        cells.element(o.add(uint(2))).assign(vec4(t1, sway));
        cells.element(o.add(uint(3))).assign(vec4(p, pickA.select(scale, scale.negate())));
        cells
          .element(o.add(uint(4)))
          .assign(vec4(rnd(8), rnd(9), fract(rnd(8).mul(7.31).add(rnd(9).mul(3.17))), 0));
      });
    })();
  }

  /** One species' material (or its light volume), drawing the trees the placement pass put down. */
  private buildMaterial(
    species: SpeciesStyle,
    isA: boolean,
    kind: 'tree' | 'light' | 'canopy' = 'tree',
    /** The species' crown (from its template), for the canopy map's disc. */
    crownShade: TreeTemplate['crown'] = null,
  ): THREE.NodeMaterial {
    const lightVolume = kind === 'light';
    const cells = this.cells;
    const vPart = varyingProperty('float', 'vTreePart');
    const vTint = varyingProperty('float', 'vTreeTint');
    const vNormal = varyingProperty('vec3', 'vTreeNormal');
    const vUp = varyingProperty('vec3', 'vTreeUp');
    const vSmooth = varyingProperty('float', 'vTreeSmooth');
    const vGround = varyingProperty('vec3', 'vTreeGround');
    // Where the fragment sits in its own tree (scaled, before the sway; the ground is at y = 0.4).
    const vLeaf = varyingProperty('vec3', 'vTreeLeaf');
    const leafy = LEAFY.includes(species.shape);
    const stony = species.shape === 'boulder';
    const gilled = species.shape === 'mushroom' && !!species.glow;
    const vLightCenter = varyingProperty('vec3', 'vLightCenter');
    const vLightRadius = varyingProperty('float', 'vLightRadius');
    const vLightUp = varyingProperty('vec3', 'vLightUp');
    // A crown disc's corner, in units of the crown's radius (canopy map).
    const vDisc = varyingProperty('vec2', 'vCrownDisc');

    const position = Fn(() => {
      const o = instanceIndex.mul(uint(CELL_VEC4));
      const c0 = cells.element(o);
      const c1 = cells.element(o.add(uint(1)));
      const c2 = cells.element(o.add(uint(2)));
      const c3 = cells.element(o.add(uint(3)));
      const c4: N = cells.element(o.add(uint(4)));
      const base = c0.xyz;
      const yaw = c0.w;
      const up = c1.xyz;
      const t1 = c2.xyz;
      // Right-handed (t1, up, t2) frame: a left-handed one mirrors the model and flips
      // its triangle winding, so back faces would be drawn instead of front faces.
      const t2 = cross(t1 as N, up as N);
      const signed = c3.w;
      const scale = (isA ? max(signed, 0) : max(signed.negate(), 0)).mul(species.scale ?? 1);

      // Every tree its own build: a little taller or squatter, wider or narrower, leaning a
      // little its own way (from the ground up), from its cell's seeded random numbers.
      const tall = c4.x.mul(2).sub(1).mul(VARY_TALL).add(1);
      const wide = c4.y.mul(2).sub(1).mul(VARY_WIDE).add(1);
      const leanAz = c4.z.mul(Math.PI * 2);
      const lean = c4.x.add(c4.y).mul(0.5).mul(LEAN);
      const shape: N = attribute('position', 'vec3');
      const rise = shape.y.sub(0.4);
      const local = vec3(
        shape.x.mul(wide).add(rise.mul(lean.mul(cos(leanAz)))),
        rise.mul(tall).add(0.4),
        shape.z.mul(wide).add(rise.mul(lean.mul(sin(leanAz)))),
      );
      const nrm = attribute('normal', 'vec3');
      const part = attribute('aPart', 'float');
      const cy = cos(yaw);
      const sy = sin(yaw);
      const rot = (v: N) => vec3(v.x.mul(cy).sub(v.z.mul(sy)), v.y, v.x.mul(sy).add(v.z.mul(cy)));
      const lp = rot(local).mul(scale);
      // Gentle sway, stronger toward the crown.
      const sway = c2.w
        .mul(local.y.mul(local.y).mul(0.004))
        .mul(scale)
        .mul(species.sway ?? 1);
      const n = rot(nrm);
      vPart.assign(part);
      vTint.assign(c1.w);
      vNormal.assign(normalize(t1.mul(n.x).add(up.mul(n.y)).add(t2.mul(n.z))));
      vUp.assign(up);
      vSmooth.assign(attribute('aSmooth', 'float'));
      vGround.assign(c3.xyz);
      // (A mushroom's own frame, unscaled and unturned: its gills run out from the stalk.)
      vLeaf.assign(gilled ? local : lp);
      if (kind === 'canopy' && crownShade) vDisc.assign(shape.xz.div(crownShade.radius));
      if (lightVolume && species.light) {
        // The light sits at the glow organ: its centre and radius grow with the plant.
        const centre = base.sub(up.mul(0.4)).add(
          up.mul(
            scale
              .mul(species.light.height - 0.4)
              .mul(tall)
              .add(scale.mul(0.4)),
          ),
        );
        vLightCenter.assign(modelWorldMatrix.mul(vec4(centre, 1)).xyz);
        vLightRadius.assign(max(scale.mul(species.light.radius), 1e-3));
        vLightUp.assign(up);
      }
      return base
        .sub(up.mul(0.4))
        .add(t1.mul(lp.x.add(sway)))
        .add(up.mul(lp.y))
        .add(t2.mul(lp.z));
    });

    if (kind === 'canopy' && this.canopy) {
      // The crown seen from straight above, as a soft disc (leaves let light through at its rim),
      // max-blended into the canopy map with the height of its underside.
      const cmat = new THREE.MeshBasicNodeMaterial({
        depthTest: false,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      cmat.blending = THREE.CustomBlending;
      cmat.blendEquation = THREE.MaxEquation;
      cmat.blendSrc = THREE.OneFactor;
      cmat.blendDst = THREE.OneFactor;
      cmat.positionNode = position();
      cmat.colorNode = this.canopy.crownColor(smoothstep(1, 0.35, length(vDisc)), positionWorld);
      return cmat;
    }
    if (lightVolume && species.light && species.glow) {
      // Deferred point light: find the scene surface behind this pixel and light it by
      // its distance to the glow organ (inverse square, windowed to the sphere).
      const lmat = new THREE.MeshBasicNodeMaterial({
        transparent: true,
        depthTest: false,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.BackSide,
      });
      lmat.positionNode = position();
      const toLight = sceneSurfacePosition().sub(vLightCenter);
      const d2 = dot(toLight, toLight);
      const w = clamp(float(1).sub(d2.div(vLightRadius.mul(vLightRadius))), 0, 1);
      // The organ itself (and its stalk top) already glow: light only what's around it.
      const clear = smoothstep(4, 16, d2);
      // A hooded organ (a lantern bulb) only lights what's below it.
      const cone = species.light.hooded
        ? smoothstep(0.3, -0.15, dot(toLight.div(max(d2.sqrt(), 1e-3)), vLightUp))
        : float(1);
      lmat.colorNode = vec4(
        species.glow().mul(species.light.gain).div(d2.add(4)).mul(w.mul(w)).mul(clear).mul(cone),
        1,
      );
      registerVolumeMaterial(lmat);
      return lmat;
    }
    const mat = new THREE.MeshStandardNodeMaterial({ roughness: 0.85, metalness: 0 });
    mat.positionNode = position();
    const bark = vec3(...species.bark);
    let crown: N = mix(vec3(...species.crown[0]), vec3(...species.crown[1]), vTint);
    if (species.seasonal) {
      const season = mix(vec3(...species.seasonal.colors[0]), vec3(...species.seasonal.colors[1]), vTint);
      crown = mix(crown, season, species.seasonal.at(vGround));
    }
    // The face's normal, from how the position changes across the pixel (the corners are
    // shared, see buildTreeGeometry), turned to the camera: back faces aren't drawn. Smooth
    // parts use the corners' interpolated normals instead.
    const pw = positionWorld;
    const face = normalize(cross(dFdx(pw), dFdy(pw)));
    const faced = face.mul(sign(dot(face, pw.negate())));
    let worldN: N = normalize(mix(faced, normalize(vNormal), vSmooth));
    const up = normalize(vUp);
    // Leaves: crowns are round (the corners' normals), and close by their surface breaks up
    // into clumps of leaves (vector noise in the tree's own frame tilts the normal), with
    // the gaps between clumps in shade. Faceted crowns read as a low-poly game's.
    let clumps: N = float(1);
    // The same place in the noise for every tree would show: each tree gets its own.
    const q = vLeaf.add(vec3(vTint.mul(53.1), vTint.mul(17.3), vTint.mul(31.7)));
    if (leafy) {
      const crownPart = step(0.5, vPart).mul(step(vPart, 1.5));
      const close = float(1).sub(smoothstep(LEAF_NEAR, LEAF_FAR, length(pw)));
      const w = crownPart.mul(close);
      const leaf = noise3When(q.mul(1 / LEAF_CLUMP), w);
      const round = normalize(vNormal);
      const tilted = normalize(round.add(leaf.mul(w.mul(LEAF_TILT))));
      worldN = mix(worldN, tilted, crownPart);
      clumps = float(1).sub(clamp(dot(leaf, round).negate(), 0, 1).mul(w.mul(LEAF_GAP_SHADE)));
      // Single leaves catching the light, only where a pixel is smaller than them.
      const veryClose = crownPart.mul(float(1).sub(smoothstep(LEAF_NEAR * 0.25, LEAF_NEAR, length(pw))));
      clumps = clumps.mul(
        noiseWhen(q.mul(1 / LEAF_SIZE), veryClose)
          .mul(veryClose.mul(0.35))
          .add(1),
      );
    }
    // Boulders: facets worn half round, with grain and weathered patches close by, and the
    // ground's shade around their foot (they sat on the ground like stones on a table).
    if (stony) {
      const close = float(1).sub(smoothstep(STONE_NEAR, STONE_FAR, length(pw)));
      const grain = noise3When(q.mul(1 / STONE_GRAIN), close);
      worldN = normalize(mix(faced, normalize(vNormal), 0.5).add(grain.mul(close.mul(0.4))));
      clumps = noiseWhen(q.mul(1 / STONE_PATCH).add(vec3(7.7, 1.3, 4.1)), close)
        .mul(close.mul(0.35))
        .add(1)
        .mul(mix(float(0.65), float(1), smoothstep(0, STONE_FOOT, vLeaf.y.sub(0.4))));
    }
    // Bark: furrows running up the trunks and branches (noise stretched along the tree's up),
    // close by only. They're round now (their corners' normals): six facets read as a low-poly game's.
    const trunkPart = float(1).sub(step(0.5, vPart));
    const barkNear = trunkPart.mul(float(1).sub(smoothstep(BARK_NEAR, BARK_FAR, length(pw))));
    clumps = clumps.mul(
      noiseWhen(q.mul(vec3(1 / BARK_FURROW, 1 / BARK_RUN, 1 / BARK_FURROW)), barkNear)
        .mul(barkNear.mul(0.5))
        .add(1),
    );
    // Sky-light gradient: faces turned down (undersides, lower trunk sides) see less of the
    // bright hollow above. Gives shape even under flat ambient light.
    const sky = mix(float(0.45), float(1), dot(worldN, up).mul(0.5).add(0.5));
    mat.colorNode = vec4(mix(bark, crown, vPart.min(1)).mul(sky).mul(clumps), 1);
    // Bend crown normals toward up: crowns lit from straight above stay readable (a cheap
    // stand-in for light scattering through leaves) and still follow day/night.
    mat.normalNode = normalize(transformToView(normalize(mix(worldN, up, vPart.min(1).mul(species.bendUp)))));
    if (species.glow) {
      let organ: N = smoothstep(1.4, 1.9, vPart);
      const crownPart = smoothstep(0.4, 0.9, vPart).sub(organ);
      if (gilled) {
        // A mushroom's gills: thin radial plates glowing between dark clefts, brighter toward
        // the cap's rim. One flat glowing disc blew out to white under the bloom. Far off, where
        // the plates would shimmer, the average of the pattern.
        const turns = atan(vLeaf.z, vLeaf.x).mul(GILLS / (2 * Math.PI));
        const rim = smoothstep(0.15, 0.9, length(vLeaf.xz).div(GILL_RADIUS));
        // Each plate a little brighter or dimmer than the next.
        const plate = fract(sin(floor(turns).mul(12.9898)).mul(43758.5453))
          .mul(0.45)
          .add(0.55);
        const plates = smoothstep(0.1, 0.6, sin(turns.mul(Math.PI)).abs()).mul(plate);
        const sharp = float(1).sub(smoothstep(15, 45, length(pw)));
        const lit = mix(float(0.45), plates, sharp).mul(0.75).add(0.15);
        organ = organ.mul(lit).mul(rim.mul(0.6).add(0.4)).mul(GILL_GLOW);
      }
      mat.emissiveNode = species
        .glow()
        .mul(organ.add(crownPart.mul(species.crownGlow ?? 0).mul(clumps)))
        .mul(vTint.mul(0.8).add(0.6));
    }
    return mat;
  }
}

/** A mushroom's gills: how many plates, and the disc's radius (m, at scale 1; see buildTreeGeometry). */
const GILLS = 64;
const GILL_RADIUS = 3.55;
/** How bright the gills glow, against the species' glow (it is the light they shed, and lit the
 * whole disc white). */
const GILL_GLOW = 0.4;

/** How far out from a cave mouth its way in is kept clear of trees (m; see mouthClearance). */
const MOUTH_PATH = 36;

/** Shapes whose crowns are leaves (part 1): round, and clumped close by. */
const LEAFY: TreeShape[] = ['conifer', 'broadleaf', 'palm', 'acacia', 'shrub'];
/** Size of a clump of leaves (m, in the tree's own scale). */
const LEAF_CLUMP = 0.5;
/** Size of a leaf's light and shade (m). */
const LEAF_SIZE = 0.16;
/** How far a clump tilts the crown's normal, and how dark the gaps between clumps get. */
const LEAF_TILT = 1;
const LEAF_GAP_SHADE = 0.6;
/** Bark furrows: their width round the trunk and length up it (m), and the range they show at (m). */
const BARK_FURROW = 0.07;
const BARK_RUN = 0.9;
const BARK_NEAR = 10;
const BARK_FAR = 40;
/** Boulders: size of their grain and weathered patches (m), how far up their foot is shaded. */
const STONE_GRAIN = 0.3;
const STONE_PATCH = 0.9;
const STONE_FOOT = 0.3;
const STONE_NEAR = 30;
const STONE_FAR = 90;
/** Distances (m) over which the clumps fade out: further off they'd only shimmer. */
const LEAF_NEAR = 40;
const LEAF_FAR = 120;

/** World normal → view space (terrain/tree meshes carry no rotation). */
function transformToView(n: N): N {
  return cameraViewMatrix.mul(vec4(n, 0)).xyz;
}

/** A tree of `shape` generated from `seed` (see treeGen), as an instanced geometry. */
export function buildTreeGeometry(shape: TreeShape, seed = 0): THREE.InstancedBufferGeometry {
  return templateGeometry(generateTree(shape, seed));
}

/** The world seed's share of the placement hash: a non-negative integer, so seed + cells stays under 2^24. */
function worldSalt(seed: number): number {
  return hashSeed(seed >>> 0, 0x7ee5) % 15_000_000;
}

/** A generated tree's parts, put together as one instanced geometry. */
function templateGeometry(template: TreeTemplate): THREE.InstancedBufferGeometry {
  const parts = template.parts;
  const pos: number[] = [];
  const nrm: number[] = [];
  const prt: number[] = [];
  const smo: number[] = [];
  const idx: number[] = [];
  let base = 0;
  for (const { geo, part, smooth } of parts) {
    // Corners are shared between faces: the faceted (low-poly) look comes from the fragment
    // shader's face normal, so each corner is placed once instead of once per face (five to
    // six times fewer vertices: 23,000 trees' vertices cost 3 ms). Smooth domes, whose poles
    // would pinch, keep the corners' averaged normals.
    const plain = new THREE.BufferGeometry();
    plain.setAttribute('position', geo.getAttribute('position'));
    if (geo.index) plain.setIndex(geo.index);
    const g = mergeVertices(plain, 1e-4);
    g.computeVertexNormals();
    const p = g.getAttribute('position');
    const n = g.getAttribute('normal');
    for (let i = 0; i < p.count; i++) {
      pos.push(p.getX(i), p.getY(i), p.getZ(i));
      nrm.push(n.getX(i), n.getY(i), n.getZ(i));
      prt.push(part);
      smo.push(smooth ? 1 : 0);
    }
    const ix = g.index as THREE.BufferAttribute;
    for (let i = 0; i < ix.count; i++) idx.push(base + ix.getX(i));
    base += p.count;
  }
  const out = new THREE.InstancedBufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  out.setAttribute('aPart', new THREE.Float32BufferAttribute(prt, 1));
  out.setAttribute('aSmooth', new THREE.Float32BufferAttribute(smo, 1));
  out.setIndex(idx);
  out.instanceCount = 0;
  out.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e9);
  return out;
}

/** A crown's disc for the canopy map: a square across the crown at its underside. */
function buildCrownDisc(shade: { bottom: number; radius: number }): THREE.InstancedBufferGeometry {
  const g = new THREE.PlaneGeometry(shade.radius * 2, shade.radius * 2);
  g.rotateX(-Math.PI / 2);
  g.translate(0, shade.bottom, 0);
  const p = g.getAttribute('position');
  const out = new THREE.InstancedBufferGeometry();
  out.setAttribute('position', p);
  out.setAttribute('normal', g.getAttribute('normal'));
  out.setAttribute('aPart', new THREE.Float32BufferAttribute(new Float32Array(p.count).fill(1), 1));
  out.setAttribute('aSmooth', new THREE.Float32BufferAttribute(new Float32Array(p.count), 1));
  if (g.index) out.setIndex(g.index);
  out.instanceCount = 0;
  out.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e9);
  return out;
}

/**
 * Light volume for a glow organ `height` metres up: an octahedron circumscribing a
 * sphere of `radius` (its faces touch the sphere), so every pixel the light can
 * reach is covered with just six corners.
 */
function buildLightGeometry(radius: number, height: number): THREE.InstancedBufferGeometry {
  const g = new THREE.OctahedronGeometry(radius * Math.sqrt(3), 0);
  g.translate(0, height, 0);
  const p = g.getAttribute('position');
  const out = new THREE.InstancedBufferGeometry();
  out.setAttribute('position', p);
  out.setAttribute('normal', g.getAttribute('normal'));
  out.setAttribute('aPart', new THREE.Float32BufferAttribute(new Float32Array(p.count).fill(2), 1));
  // (Read by the trees' vertex code, which these share.)
  out.setAttribute('aSmooth', new THREE.Float32BufferAttribute(new Float32Array(p.count), 1));
  out.instanceCount = 0;
  out.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e9);
  return out;
}
